import styled from 'styled-components';

const MainWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 100vw;
    height: 100vh;
    position: relative;
    overflow: hidden;
`;

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 32px;
    width: 100%;

    img {
        height: 48px;
        width: auto;
        object-fit: contain;
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
    }

    @media (max-width: 767px) {
        margin-bottom: 24px;
        
        img {
            height: 40px;
        }
    }
`;

const BoxHeaderImage = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60vh;
    z-index: 0;

    border-radius: 0 0 20px 20px;
    overflow: hidden;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    z-index: 2;
    padding-bottom: 48px;
    
    @media (max-width: 767px) {
        display: none;
    }
`;

const DynamicTitleAnimatedWordWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 530px;
    height: 58px;
    
    div.word-container {
        width: 530px;
        height: 58px;
        position: absolute;
        opacity: 0;
        display: flex;
        justify-content: center;
    }

    div.word-container.visible {
        opacity: 1;
        transition: opacity 0.5s ease-in-out;
    }

    div.word-container span {
        font-family: 'Inter', sans-serif;
        font-feature-settings: 'clig' off, 'liga' off;
        font-size: var(--text-4xl, 48px);
        font-weight: var(--font-weight-semibold, 600);
        font-style: normal;
        line-height: 1.5;
        letter-spacing: -1px;
        color: var(--brand-light, #FFFFFF);
        opacity: 0;
    }

    div.word-container span.animate {
        animation: appear 0.5s forwards;
    }

    @keyframes appear {
        to {
            opacity: 1;
        }
    }
    
`;

const WrapperForgotPassword = styled.div`
    text-align: right;
`;

const FormWrapper = styled.div`
    display: flex;
    width: 530px;
    min-height: 460px;
    max-height: 550px;
    padding: 24px 48px;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;
    z-index: 5;
    position: relative;

    @media (max-width: 767px) {
        width: 42vh;
        max-width: 100%;
        padding: 20px;
        min-height: auto;
        max-height: none;
    }
`;

const FormOrDivider = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 17px;
    align-items: center;
    justify-content: space-around;
    
    div {
        width: 100%;
        border-top: 1px solid ${props => props.theme.colors.silverGrayTwo};
        height: 2px;
    }
    
    span {
        ${(props) => props.theme.fonts.variant.regular()};
        color: ${props => props.theme.colors.greyEleven};
        font-size: 1em;
        line-height: normal;
        font-style: normal;
    }
`;

const FormSocialNetworks = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
    height: 48px;
    padding: 24px 0;
`;

const SocialButton = styled.a`
    border-radius: 8px;
    border: 1px solid ${props => props.theme.colors.silverGrayTwo};
    width: 100%;
    display: flex;
    height: 40px;
    padding: 10px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    flex: 1 0 0;
    opacity: 0.7;
    
    &:hover {
        cursor: pointer;
        opacity: 1;
    }
`;

const FormTitle = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: var(--text-2xl, 24px);
    font-weight: var(--font-weight-bold, 700);
    color: var(--brand-light, #FFFFFF);
    padding: 0 0 24px 0;
    font-style: normal;
    line-height: 1.5;
    text-align: center;
`;

const FormSubtitle = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: var(--text-base, 14px);
    font-weight: var(--font-weight-normal, 400);
    color: var(--brand-light, #FFFFFF);
    font-feature-settings: 'clig' off, 'liga' off;
    padding: 0 0 24px 0;
    font-style: normal;
    line-height: 20px;
    text-align: center;
    opacity: 0.9;
`;

const FormFooter = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-top: 32px;
    width: 530px;
    gap: 12px;
    
    a {
        font-family: 'Inter', sans-serif;
        font-size: var(--text-base, 14px);
        font-weight: var(--font-weight-normal, 400);
        color: var(--brand-light, #FFFFFF);
        opacity: 0.7;
        line-height: 1.5;
        font-style: normal;
        
        &:hover {
            cursor: pointer;
            opacity: 1;
        }
    }

    div.divider {
        width: 3px;
        height: 3px;
        background-color: rgba(255, 255, 255, 0.3);
    }

    @media (max-width: 767px) {
        display: none;
    }
`;

const Footer = styled.div`
    position: absolute;
    bottom: 67px;
    display: flex;
    align-items: center;
    font-family: 'Inter', sans-serif;
    font-size: var(--text-xl, 18px);
    font-weight: var(--font-weight-normal, 400);
    color: var(--muted-foreground, rgba(186, 186, 186, 1));
    letter-spacing: 0.15px;
    line-height: 1.5;

    @media (max-width: 767px) {
        display: absolute;
    }
`;

const WithAimsText = styled.div`
    text-align: center;
    font-family: 'Inter', sans-serif;
    font-size: var(--text-base, 14px);
    font-weight: var(--font-weight-normal, 400);
    color: var(--brand-light, #FFFFFF);
    line-height: 1.5;
`;

const SignUpLink = styled.a`
    color: var(--brand-light-blue, #00B5D9);
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
    
    &:hover {
        opacity: 0.8;
        text-decoration: underline;
    }
`;

export {
    MainWrapper,
    LogoContainer,
    WrapperForgotPassword,
    FormWrapper,
    FormTitle,
    FormSubtitle,
    FormOrDivider,
    FormSocialNetworks,
    SocialButton,
    FormFooter,
    Footer,
    SignUpLink
}